import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useContext, useEffect, useState } from "react";
import { ConfigContext, OrderDataContext, ReturnOrderContext } from "@/Layout";
import { useCalculateReturn } from "@/api/calculateReturn";
import { useUpsellProducts } from "@/api/upsellProducts";
import { AnimatedCurrency } from "@/components/AnimatedCurrency/AnimatedCurrency";
import { Cart } from "@/components/Cart/Cart";
import LoadSpinner from "@/components/LoadSpinner/LoadSpinner";
import { ReturnContainer } from "@/components/ReturnContainer/ReturnContainer";
import { UpsellProduct } from "@/components/UpsellProduct/UpsellProduct";
import { useDebounce } from "@/utils/utils";

export const Route = createFileRoute("/upsell-explore")({
  component: RouteComponent,
});

function RouteComponent() {
  const [search, setSearch] = useState("");
  const [searchMode, setSearchMode] = useState(false);
  const debouncedSearch = useDebounce(search, 200);
  const { data: products, isLoading } = useUpsellProducts(
    searchMode ? debouncedSearch : "",
    { enabled: true }
  );
  const { orderData } = useContext(OrderDataContext);
  const configData = useContext(ConfigContext);
  const { returnOrder } = useContext(ReturnOrderContext);
  const [collapsed, setCollapsed] = useState(true);
  const { data: calculatedReturn, isLoading: isCalculatingReturn } =
    useCalculateReturn({ enabled: true });
  const navigate = useNavigate();

  useEffect(() => {
    if (!returnOrder) {
      navigate({
        to: "/",
        search: (prev) => prev,
      });
    }
  }, [returnOrder, navigate]);

  if (!returnOrder) return null;

  const nextRoute = () => {
    if (!calculatedReturn) return;
    if (
      calculatedReturn.refund_value.presentment_money.amount > 0 &&
      orderData?.gift_card_value_multiplier
    ) {
      navigate({
        to: "/refund-method",
        search: (prev) => prev,
      });
    } else {
      navigate({
        to: "/confirm",
        search: (prev) => prev,
      });
    }
  };

  const productsComponent =
    products && products.length > 0 ? (
      products.map((product) => (
        <UpsellProduct key={product.id} product={product} />
      ))
    ) : (
      <p className="text-sm">No products found</p>
    );

  const searchIcon = (
    <svg
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clip-path="url(#clip0_743_4621)">
        <path
          // eslint-disable-next-line max-len
          d="M11.7429 10.8441C12.7112 9.52279 13.1449 7.88459 12.9572 6.25725C12.7695 4.62991 11.9743 3.13344 10.7307 2.06723C9.48701 1.00102 7.88665 0.443704 6.24973 0.506777C4.61282 0.56985 3.06008 1.24866 1.90217 2.40741C0.744249 3.56615 0.0665484 5.11938 0.00464653 6.75633C-0.0572553 8.39329 0.501207 9.99326 1.56831 11.2361C2.6354 12.479 4.13244 13.2732 5.75992 13.4597C7.38739 13.6462 9.02528 13.2113 10.3459 12.2421H10.3449C10.3742 12.2821 10.4069 12.3204 10.4429 12.3571L14.2929 16.2071C14.4804 16.3947 14.7348 16.5002 15 16.5003C15.2653 16.5004 15.5198 16.3951 15.7074 16.2076C15.895 16.0201 16.0005 15.7657 16.0006 15.5005C16.0007 15.2352 15.8954 14.9807 15.7079 14.7931L11.8579 10.9431C11.8222 10.9069 11.7837 10.8745 11.7429 10.8441ZM12.0009 7.0001C12.0009 7.72237 11.8586 8.43757 11.5822 9.10486C11.3058 9.77215 10.9007 10.3785 10.39 10.8892C9.87926 11.3999 9.27295 11.805 8.60566 12.0814C7.93837 12.3578 7.22317 12.5001 6.5009 12.5001C5.77863 12.5001 5.06343 12.3578 4.39614 12.0814C3.72885 11.805 3.12253 11.3999 2.61181 10.8892C2.10109 10.3785 1.69596 9.77215 1.41956 9.10486C1.14316 8.43757 1.0009 7.72237 1.0009 7.0001C1.0009 5.54141 1.58036 4.14246 2.61181 3.11101C3.64326 2.07956 5.04221 1.5001 6.5009 1.5001C7.95959 1.5001 9.35853 2.07956 10.39 3.11101C11.4214 4.14246 12.0009 5.54141 12.0009 7.0001Z"
          fill="#505050"
        />
      </g>
    </svg>
  );

  return (
    <ReturnContainer>
      <div className="sm:min-w-[400px] md:min-w-[600px] lg:min-w-[800px] max-w-[800px]">
        <h1 className="font-semibold text-lg">
          {configData?.upsell_explore_header_text ?? "Explore"}
        </h1>
        <p className="text-sm mt-2">
          {configData?.upsell_explore_message ??
            "Find something else from the store"}
        </p>
        <hr className="mb-4 mt-4" />
        <div className="w-full relative">
          <div className="absolute left-2 top-1/2 -translate-y-1/2">
            {searchIcon}
            <div className="min-h-1"></div>
          </div>
          <input
            value={search}
            onChange={(e) => {
              setSearchMode(true);
              setSearch(e.target.value);
            }}
            type="text"
            placeholder={configData?.upsell_explore_search_text ?? "Search"}
            className="w-full border bg-gray-50 text-black rounded-sm p-2 pl-8 text-sm"
          />
        </div>
        <div className="grid sm:flex sm:justify-between mt-2">
          <div className="flex gap-2 sm:text-right text-xs font-bold ml-auto">
            <span>
              {(configData?.available_return_credits_text ??
                "Available return credits") + ":"}
            </span>
            <div className="min-w-6">
              {isCalculatingReturn && (
                <LoadSpinner extraClassNames="max-w-4 max-h-4" />
              )}
              {!isCalculatingReturn && calculatedReturn ? (
                <AnimatedCurrency
                  value={
                    calculatedReturn.available_for_exchange.presentment_money
                      .amount ?? 0
                  }
                  currency={
                    calculatedReturn.refund_value.presentment_money
                      .currency_code
                  }
                />
              ) : null}
            </div>
          </div>
        </div>
        <hr className="mt-1 mb-4" />
        <div
          className="grid grid-cols-2 sm:grid-cols-5 gap-x-4  gap-y-4 place-items-start max-h-96 overflow-y-auto pr-2"
          id="upsell-product-grid"
        >
          {isLoading ? <LoadSpinner /> : productsComponent}
        </div>
        <hr className="my-4" />
        <div className="mt-4">
          <Cart
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            canContinue={!isCalculatingReturn}
            onContinue={() => nextRoute()}
          />
        </div>
      </div>
    </ReturnContainer>
  );
}
